/**
 * The shared order of a setlist / folder (pure — unit tested: tests/unit/collection-order.test.ts).
 *
 * Saving replaces the WHOLE list on the server (PUT /collections/:id/entries), so a reorder must send
 * every entry of the editor's unfiltered CollectionDTO.entries — repeats, songs the editor's song
 * list does not show and notes included — with entryId, songId and note unchanged; only the order
 * differs. `baseUpdatedAt` makes the server refuse (409) a list built on an older version.
 */
import type { CollectionEntryDTO, SetCollectionEntriesRequest } from '../../../shared/dto/library';

/** `list` with the item at `from` moved to index `to`; the same array when either index is out of range or equal. */
export function moveTo<T>(list: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) return list;
  const next = [...list];
  const [it] = next.splice(from, 1);
  next.splice(to, 0, it!);
  return next;
}

/** One step up (-1) or down (+1); the same array at the ends. */
export function move<T>(list: T[], index: number, dir: -1 | 1): T[] {
  return moveTo(list, index, index + dir);
}

type EntryLike = { entryId: string; songId: string; note?: string | null };

/** Same entries in the same order (entry ids only — notes and songs never change in a reorder). */
export function sameOrder(a: readonly Pick<EntryLike, 'entryId'>[], b: readonly Pick<EntryLike, 'entryId'>[]): boolean {
  return a.length === b.length && a.every((x, i) => x.entryId === b[i]!.entryId);
}

const noteOf = (e: EntryLike) => (e.note ?? '').trim();

/**
 * Same list in every respect a save would write (entry, song, note, order). A draft built on `a` can
 * safely be saved over `b` when this holds — e.g. only the name changed (which also moves updatedAt).
 */
export function sameEntries(a: readonly EntryLike[], b: readonly EntryLike[]): boolean {
  return a.length === b.length && a.every((x, i) => x.entryId === b[i]!.entryId && x.songId === b[i]!.songId && noteOf(x) === noteOf(b[i]!));
}

/** Entries in list order (position, then entry id for stable ties — as the viewer orders them). */
export function inListOrder(entries: readonly CollectionEntryDTO[]): CollectionEntryDTO[] {
  return [...entries].sort((a, b) => a.position - b.position || (a.entryId < b.entryId ? -1 : a.entryId > b.entryId ? 1 : 0));
}

/**
 * The PUT body for `ordered` (every entry, in the new order), guarded by `baseUpdatedAt` when given.
 * Notes are trimmed and an empty one is null — exactly what the server stores.
 */
export function toEntriesRequest(ordered: readonly EntryLike[], baseUpdatedAt?: number): SetCollectionEntriesRequest {
  const entries = ordered.map((e) => ({ entryId: e.entryId, songId: e.songId, note: noteOf(e) || null }));
  return baseUpdatedAt === undefined ? { entries } : { entries, baseUpdatedAt };
}

/**
 * What a refused save (409: the list's version moved) means, judged from the list's `latest` entries:
 *  - 'saved': the list already is what was `sent` — an earlier save of it went through and only its
 *    answer was lost (flaky Wi-Fi), or someone saved the very same list;
 *  - 'retry': the entries are still the `base` the draft was built on — only the name / description /
 *    kind changed (that moves the version too) — so `sent` can be saved again on the new version;
 *  - 'stale': someone else changed the songs: show theirs rather than overwrite them.
 * `base` null = no retry (already retried once).
 */
export function afterConflict(latest: readonly EntryLike[], sent: readonly EntryLike[], base: readonly EntryLike[] | null): 'saved' | 'retry' | 'stale' {
  if (sameEntries(latest, sent)) return 'saved';
  if (base && sameEntries(latest, base)) return 'retry';
  return 'stale';
}
